import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { MONTH_NAMES } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';

/** Route: `/payroll/new`. */
export function PayrollPeriodFormPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const now = new Date();
  const [form, setForm] = useState({ year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 });

  const create = useMutation({
    mutationFn: () => api.post<any>('/payroll/periods', form),
    onSuccess: (period) => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      navigate(`/payroll/${period.id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('payroll.newPeriod')}
      backTo="/payroll"
      backLabel={t('nav.payroll')}
      submitLabel={t('common.create')}
      submitting={create.isPending}
      onSubmit={() => create.mutate()}
    >
      <FormGrid>
        <Field label={t('common.year')} required>
          <Input
            type="number"
            value={form.year}
            onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
            autoFocus
          />
        </Field>
        <Field label={t('common.month')} required>
          <Select value={form.month} onChange={(e) => setForm({ ...form, month: Number(e.target.value) })}>
            {MONTH_NAMES[locale].map((name, index) => (
              <option key={name} value={index + 1}>{name}</option>
            ))}
          </Select>
        </Field>
      </FormGrid>
    </FormPage>
  );
}

/**
 * §20 — a correction to a calculated entry is posted as an adjustment rather
 * than by editing the calculation. Route: `/payroll/:id/entries/:entryId/adjust`.
 */
export function PayrollAdjustmentFormPage() {
  const { id = '', entryId = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');

  const save = useMutation({
    mutationFn: () => api.post('/payroll/adjustments', { payrollEntryId: entryId, amount, description }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payroll'] });
      navigate(`/payroll/${id}`);
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <FormPage
      title={t('payroll.adjustment')}
      backTo={`/payroll/${id}`}
      backLabel={t('nav.payroll')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={!description || amount === 0}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('common.amount')} required hint={t('payroll.adjustmentHint')}>
          <Input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            autoFocus
          />
        </Field>
        <Field label={t('common.description')} required>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
    </FormPage>
  );
}
